import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, Check, Loader2, Skull, Sparkles, Gift, Repeat, Crown,
  ArrowRight, Copy, CheckCircle2, Clock, X, Zap,
} from "lucide-react";
import { toast } from "sonner";

/*  Mystery Club — $9/month recurring subscription.
    Routes:
      /club                  → landing + checkout (?code=X pre-applies promo)
      /club/thanks           → polls Stripe session → redirect to /club/members/:token
      /club/members/:token   → private member page: library, coupon, cancel link
*/

const NUDGE_WINDOW_HOURS = 48;

const Club = () => {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const promoCode = (params.get("code") || "").toUpperCase();
  const fromNudge = params.get("from") === "teaser_nudge";
  const [nudgeExpiry, setNudgeExpiry] = useState(null);
  const [countdownStr, setCountdownStr] = useState("");

  useEffect(() => {
    api.get("/club/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  // Compute a visitor-local 48h countdown anchored on first visit with ?code=
  // Stored in sessionStorage so the countdown doesn't reset on page reload.
  useEffect(() => {
    if (!promoCode || !fromNudge) return;
    const key = `club_nudge_expiry:${promoCode}`;
    let iso = sessionStorage.getItem(key);
    if (!iso) {
      iso = new Date(Date.now() + NUDGE_WINDOW_HOURS * 3600 * 1000).toISOString();
      sessionStorage.setItem(key, iso);
    }
    setNudgeExpiry(iso);
  }, [promoCode, fromNudge]);

  useEffect(() => {
    if (!nudgeExpiry) return;
    const tick = () => {
      const ms = new Date(nudgeExpiry).getTime() - Date.now();
      if (ms <= 0) { setCountdownStr("expired"); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setCountdownStr(`${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nudgeExpiry]);

  const subscribe = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const payload = { email, name, origin_url: window.location.origin };
      if (promoCode) payload.promo_code = promoCode;
      const { data } = await api.post("/club/checkout", payload);
      if (data.already_active && data.member_url) {
        window.location.href = data.member_url;
        return;
      }
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const price = status?.price_label || "$9";
  const discount = status?.discount_pct || 20;
  const activeMembers = status?.active_members ?? 0;
  const showNudgeOffer = promoCode === "TEASERCLUB" && countdownStr && countdownStr !== "expired";

  return (
    <div className="grain min-h-screen" data-testid="club-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-8">
          <ArrowLeft size={16} /> back to the file
        </Link>

        {/* Teaser → Club nudge countdown banner */}
        {showNudgeOffer && (
          <div className="card-noir p-5 mb-6 border-[#d4af37] bg-gradient-to-r from-[#1a1413] to-[#0a0908]"
            data-testid="club-nudge-banner">
            <div className="flex items-center gap-2 mb-2">
              <Zap size={14} className="text-[#d4af37]" />
              <div className="text-xs tracking-widest text-[#d4af37]">LIMITED · TEASER OFFER</div>
            </div>
            <div className="font-display text-2xl text-[#f4f0ec] mb-1">
              50% off your first month
            </div>
            <div className="text-sm text-[#a39797] mb-2">
              Code <span className="font-mono text-[#d4af37]" translate="no">{promoCode}</span> is pre-applied below. After the first month: regular $9/mo. Cancel anytime.
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock size={14} className="text-[#ff6b6b]" />
              <span className="text-[#a39797]">Expires in</span>
              <span className="font-mono text-[#ff6b6b] font-bold" translate="no" data-testid="club-nudge-countdown">
                {countdownStr}
              </span>
            </div>
          </div>
        )}

        <div className="mb-10 text-center">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-3">members only · monthly</div>
          <h1 className="font-display text-5xl sm:text-6xl text-[#f4f0ec] mb-4">The Mystery Club</h1>
          <p className="text-[#c7bebe] text-lg max-w-xl mx-auto">
            A new case highlighted every month. {discount}% off everything.
            Cancel any time from your member page.
          </p>
        </div>

        {activeMembers > 0 && (
          <div className="card-noir p-3 mb-6 text-center text-xs text-[#a39797]" data-testid="club-social-proof">
            <Crown size={12} className="inline text-[#d4af37] mr-1" />
            <span className="text-[#f4f0ec] font-bold" translate="no">{activeMembers}</span> active member{activeMembers !== 1 ? "s" : ""}
          </div>
        )}

        {/* Benefits */}
        <div className="card-noir p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-[#d4af37]" />
            <div className="font-display text-xl text-[#f4f0ec]">What's inside</div>
          </div>
          <ul className="space-y-3 text-sm text-[#c7bebe]">
            <li className="flex gap-3"><Gift size={16} className="text-[#4ade80] shrink-0 mt-0.5" />
              <span><b className="text-[#f4f0ec]">Member case of the month</b> — a case highlighted and spotlit for members in your private library.</span></li>
            <li className="flex gap-3"><Check size={16} className="text-[#4ade80] shrink-0 mt-0.5" />
              <span><b className="text-[#f4f0ec]">{discount}% off every case</b>, Party Pack, Case Maker build, and Crew Night upgrade. Your personal code works forever.</span></li>
            <li className="flex gap-3"><Sparkles size={16} className="text-[#4ade80] shrink-0 mt-0.5" />
              <span><b className="text-[#f4f0ec]">Early access</b> to Global Mystery Night case drops before public release.</span></li>
            <li className="flex gap-3"><Repeat size={16} className="text-[#4ade80] shrink-0 mt-0.5" />
              <span><b className="text-[#f4f0ec]">Cancel anytime</b> from the Stripe billing portal, right from your member page. No contract.</span></li>
          </ul>
        </div>

        {/* Checkout */}
        <form onSubmit={subscribe} className="card-noir p-6 border-[#d4af37]" data-testid="club-form">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <div>
              <div className="text-xs tracking-widest text-[#a39797]">JOIN THE CLUB</div>
              <div className="font-display text-xl text-[#f4f0ec] mt-1">Monthly membership</div>
              {showNudgeOffer && (
                <div className="text-xs text-[#d4af37] mt-1" data-testid="club-form-promo-note">
                  Promo <span className="font-mono" translate="no">{promoCode}</span> applied at checkout
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="font-display text-4xl text-[#d4af37]" translate="no">{price}</div>
              <div className="text-xs text-[#a39797]">/ month · cancel anytime</div>
            </div>
          </div>
          <input type="email" required placeholder="Your email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-3 focus:outline-none focus:border-[#d4af37]"
            data-testid="club-email" />
          <input placeholder="Your name (optional)"
            value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-4 focus:outline-none focus:border-[#d4af37]"
            data-testid="club-name" />
          <button type="submit" disabled={busy}
            className="btn-primary w-full justify-center disabled:opacity-50"
            data-testid="club-subscribe-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Skull size={14} />}
            {showNudgeOffer
              ? `Start at 50% off — first month $4.50`
              : `Start — ${price}/month`}
          </button>
          <div className="text-xs text-[#a39797] mt-3 text-center">
            Secure recurring checkout via Stripe. We never see your card.
          </div>
        </form>

        <p className="text-center text-xs text-[#5a5050] mt-8">
          Prefer a one-time payment? <Link to="/founder" className="text-[#d4af37]">Become a Founding Member</Link> instead — lifetime access, $99 once.
        </p>
      </div>
    </div>
  );
};

export const ClubThanks = () => {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("polling");
  const [memberUrl, setMemberUrl] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!sid) { setState("missing"); return; }
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/club/thanks?session_id=${sid}`);
        if (data.paid && data.member_url) {
          setMemberUrl(data.member_url);
          setState("ready");
          setTimeout(() => navigate(`/club/members/${data.member_token}`), 1500);
          return;
        }
      } catch (err) { /* retry */ }
      tries += 1;
      if (tries < 20) setTimeout(tick, 1500);
      else setState("timeout");
    };
    tick();
  }, [sid, navigate]);

  return (
    <div className="grain min-h-screen flex items-center justify-center">
      <div className="card-noir p-8 max-w-md text-center" data-testid="club-thanks">
        {state === "polling" && (
          <>
            <Loader2 size={28} className="mx-auto animate-spin text-[#d4af37] mb-3" />
            <div className="font-display text-xl text-[#f4f0ec] mb-2">Activating your membership…</div>
            <div className="text-xs text-[#a39797]">Stripe is confirming the subscription.</div>
          </>
        )}
        {state === "ready" && (
          <>
            <Crown size={32} className="mx-auto text-[#d4af37] mb-3" />
            <div className="font-display text-2xl text-[#f4f0ec] mb-2">Welcome to the Club.</div>
            <a href={memberUrl} className="btn-primary text-sm" data-testid="club-open-member">
              Open my member page <ArrowRight size={14} />
            </a>
          </>
        )}
        {state === "timeout" && (
          <>
            <div className="font-display text-xl text-[#f4f0ec] mb-2">Taking longer than expected</div>
            <div className="text-xs text-[#a39797] mb-3">
              Payment confirmed but we're still provisioning. Check your email in 2 minutes.
            </div>
            <Link to="/" className="btn-ghost text-sm">Back home</Link>
          </>
        )}
        {state === "missing" && (
          <>
            <div className="font-display text-xl text-[#f4f0ec] mb-2">No session found</div>
            <Link to="/club" className="btn-ghost text-sm">Back to the Club</Link>
          </>
        )}
      </div>
    </div>
  );
};

export const ClubMember = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    api.get(`/club/members/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Member link not found."));
  }, [token]);

  const copyCode = async () => {
    const code = data?.member?.coupon_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy.");
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data: p } = await api.post("/club/portal", {
        member_token: token,
        return_url: window.location.href,
      });
      if (p.portal_url) window.location.href = p.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the billing portal.");
      setPortalBusy(false);
    }
  };

  if (err) {
    return (
      <div className="grain min-h-screen flex items-center justify-center p-6">
        <div className="card-noir p-8 max-w-md text-center">
          <X size={28} className="mx-auto text-[#ff6b6b] mb-3" />
          <div className="font-display text-xl text-[#f4f0ec] mb-2">{err}</div>
          <Link to="/club" className="btn-ghost text-sm">Back to the Club</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#d4af37]" />
      </div>
    );
  }

  const { member, cases } = data;
  const canceled = member.status === "canceled";
  const pastDue = member.status === "past_due";

  return (
    <div className="grain min-h-screen" data-testid="club-member-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-6">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Crown size={20} className="text-[#d4af37]" />
          <div className="text-xs tracking-widest text-[#a39797]">MYSTERY CLUB MEMBER</div>
        </div>
        <h1 className="font-display text-4xl text-[#f4f0ec] mb-6">
          Welcome back{member.name ? `, ${member.name}` : ""}.
        </h1>

        {canceled && (
          <div className="card-noir p-4 mb-4 border-[#ff6b6b]" data-testid="club-canceled-banner">
            <div className="text-sm text-[#ff6b6b] font-bold">Membership canceled</div>
            <div className="text-xs text-[#a39797]">
              You still have access until the end of your billed period. Rejoin anytime from{" "}
              <Link to="/club" className="text-[#d4af37]">/club</Link>.
            </div>
          </div>
        )}

        {pastDue && (
          <div className="card-noir p-4 mb-4 border-[#d4af37]">
            <div className="text-sm text-[#d4af37] font-bold flex items-center gap-2">
              <Clock size={14} /> Payment past due
            </div>
            <div className="text-xs text-[#a39797]">
              Update your card from the Stripe billing portal below so you don't lose access.
            </div>
          </div>
        )}

        {/* Coupon card */}
        <div className="card-noir p-6 mb-6 border-[#d4af37]" data-testid="club-coupon-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs tracking-widest text-[#a39797]">YOUR {member.discount_pct}% OFF CODE</div>
              <div className="font-display text-3xl text-[#d4af37] mt-1 tracking-widest" translate="no" data-testid="club-coupon-code">
                {member.coupon_code}
              </div>
              <div className="text-xs text-[#a39797] mt-1">Works on every case, pack, Case Maker build, and upgrade.</div>
            </div>
            <button onClick={copyCode} className="btn-ghost text-sm" data-testid="club-copy-coupon">
              {copied ? <CheckCircle2 size={14} className="text-green-400" /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy code"}
            </button>
          </div>
        </div>

        {/* Member cases */}
        <div className="card-noir p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Gift size={16} className="text-[#d4af37]" />
            <div className="font-display text-xl text-[#f4f0ec]">The Vault</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(cases || []).map((c) => (
              <Link key={c.case_id} to="/" state={{ caseId: c.case_id }}
                className="card-noir p-4 hover:border-[#d4af37] transition-colors"
                data-testid={`club-case-${c.case_id}`}>
                <div className="text-[11px] text-[#5a5050] tracking-widest">CASE #{c.case_id}</div>
                <div className="font-display text-lg text-[#f4f0ec]" translate="no">{c.title || c.name}</div>
                {c.tagline && <div className="text-xs text-[#a39797] mt-1">{c.tagline}</div>}
              </Link>
            ))}
          </div>
        </div>

        {/* Manage subscription */}
        {!canceled && (
          <div className="card-noir p-6">
            <div className="font-display text-lg text-[#f4f0ec] mb-2">Manage subscription</div>
            <p className="text-xs text-[#a39797] mb-4">
              Update your card, download invoices, or cancel. You keep access until the end of the current billed period.
            </p>
            <button onClick={openPortal} disabled={portalBusy || !member.has_stripe_portal}
              className="btn-ghost text-sm disabled:opacity-50" data-testid="club-portal-btn">
              {portalBusy ? <Loader2 size={14} className="animate-spin" /> : <Repeat size={14} />}
              Open billing portal
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Club;
